import { TableColumn } from 'components/Table';
import selectors from 'state/selectors';
import store from 'state/store';
import utils from 'utils';

/**
 * this function download a file from a base64 string
 * @param {string} filename the name of the file to be downloaded
 * @param {string} base64  the base64 string of the file
 * @param {string} contentType the content type of the file
 * @param {string} extension  the extension of the file
 */
const downloadFileFromBase64Source = (filename: string, base64: string, contentType: string, extension?: string) => {
  const linkSource = `data:${contentType};base64,${base64}`;
  const downloadLink = document.createElement('a');
  downloadLink.href = linkSource;
  const ext = extension ? `.${extension}` : '';
  downloadLink.download = `${filename}${ext}`;
  downloadLink.click();
};

const toBase64 = (file: File, dataPrefix = false): Promise<FileReader['result'] | Error> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () =>
      resolve(typeof reader.result === 'string' && !dataPrefix ? reader.result.split(',')[1] : reader.result);
    reader.onerror = (error) => reject(error);
  });

/**
 * This function download a csv file string to be downloaded as csv
 * @param {string} filename
 * @param {string} csv
 * @return {void}
 */
const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob([csv], { type: 'text/csv' });
  const csvUrl = window.webkitURL.createObjectURL(blob);
  const downloadLink = document.createElement('a');
  downloadLink.href = csvUrl;
  const date = new Date().getTime();
  downloadLink.download = `${filename}_${date}.csv`;
  downloadLink.click();
};


const escapeCsvValue = (value: any, csvSep: string): string => {
  if (value === null || value === undefined) {
    return '';
  }
  const stringValue = String(value);
  // Escape double quotes by doubling them
  const escapedValue = stringValue.replace(/"/g, '""');
  // Wrap in quotes if it contains special characters
  if (stringValue.includes(csvSep) || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${escapedValue}"`;
  }
  return escapedValue;
};

/**
 *  function utilized to transform table rows to csv
 * @param array the array of rows
 * @param columns the columns of the table
 * @param endOfFile the end of file separator
 * @param defaultCsv the default csv string
 * @return {string}
 */

const arrayToCsv = (
  array: { original: { [key: string]: any; subRows?: any[] } }[],
  columns: {
    accessor: TableColumn['accessor'];
    id?: string;
    Visible?: boolean;
    Header: string;
    [key: string]: any;
  }[],
  specialColumns?: { [key: string]: ((row: any) => string) | null },
  endOfFile = '\n',
  defaultCsv = '',
) => {
  const { csvSep, timeZone } = selectors.user.getUserPreference(store.getState());

  let csv = defaultCsv;
  const visibleColumns = columns?.filter((column) => column.isVisible !== false);
  csv += visibleColumns.map((column) => escapeCsvValue(column.Header, csvSep)).join(csvSep) + endOfFile;

  const accessors = visibleColumns?.map(({ accessor, id }) => {
    if (typeof accessor === 'string') {
      return accessor;
    } else {
      return id ?? '';
    }
  });

  array.forEach((row) => {
    const originalRow = row?.original;
    const addRowToCsv = (rowData: any) => {
      csv +=
        accessors
          ?.map((key) => {
            if (specialColumns?.hasOwnProperty(key)) {
              return escapeCsvValue(specialColumns[key]?.(rowData), csvSep);
            } else {
              if (visibleColumns.find((el) => el.accessor === key)?.filterType === 'date') {
                return escapeCsvValue(utils.date.convertDate(rowData[key], timeZone ?? 'UTC'), csvSep);
              }
              return escapeCsvValue(rowData[key], csvSep);
            }
          })
          .join(csvSep) + endOfFile;
    };

    if (originalRow.subRows?.length) {
      originalRow.subRows.forEach(addRowToCsv);
    } else {
      addRowToCsv(originalRow);
    }
  });
  return csv;
};

/**
 * This function return a string to be downloaded as csv
 * @param {Array} header
 * @param {Array} body
 * @param {string} separator
 * @return {string}
 */
const convertToCSV = (
  header: { accessor: string; label: string; converter?: (value: string) => string }[],
  body: { [key: string]: string }[],
  separator?: string,
) => {
  const { csvSep } = selectors.user.getUserPreference(store.getState());
  let csv = '';
  const EOF = '\r\n';
  // head of the csv file, the head
  const head = header.reduce((accumulator, { label }, index) => {
    return `${accumulator}${index !== 0 ? separator || csvSep : ''}"${label}"`;
  }, '');
  csv = head + EOF;

  body.forEach((row) => {
    let line = '';
    header.forEach(({ accessor, converter }) => {
      if (line !== '') line += separator || csvSep;
      line += `"${converter?.(String(row[accessor])) ?? row[accessor] ?? ''}"`;
    });
    csv += line + EOF;
  });
  return csv;
};

const openPdfInNewTab = (docImage: string) => {
  if (!docImage || typeof docImage !== 'string') {
    console.error('Invalid docImage: must be a non-empty string');
    return;
  }
  const trimmedDocImage = docImage.trim();

  if (trimmedDocImage.length === 0) {
    console.error('Invalid docImage: empty string after trimming');
    return;
  }
  try {
    const byteCharacters = atob(trimmedDocImage);
    if (byteCharacters.length === 0) {
      console.error('Invalid docImage: decoded base64 is empty');
      return;
    }

    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const file = new Blob([byteArray], { type: 'application/pdf;base64' });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  } catch (error) {
    console.error('Error processing docImage:', error);
    console.error('Invalid base64 string provided');
  }
};

const downloadFileZip = async (data: BlobPart, fileName?: string) => {
  if (data instanceof Blob) {
    try {
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'file.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('There was an error downloading the file:', error);
    }
  } else {
    console.error('The data is not a Blob');
  }
};

export default {
  toBase64,
  openPdfInNewTab,
  downloadFileFromBase64Source,
  convertToCSV,
  downloadCsv,
  arrayToCsv,
  downloadFileZip,
};
